<script setup lang="ts">
import useLocale from '../helpers/use-locale';
import MdiFaIcon from '../MdiFaIcon.vue';

type Props = {
  title: string;
  isGrabbed?: boolean;
  /**
   * Makes the grip itself the keyboard target. Lists put focus on the row instead, but a table's
   * rows are not focusable, so there the grip has to carry pick up, move and put down on its own.
   */
  focusable?: boolean;
};
const props = withDefaults(defineProps<Props>(), { focusable: false });

type Emits = {
  (e: 'toggle'): void;
  (e: 'move', direction: -1 | 1): void;
};
const emit = defineEmits<Emits>();

const onArrow = (direction: -1 | 1) => {
  if (!props.focusable || !props.isGrabbed) {
    return;
  }
  emit('move', direction);
};

const { t } = useLocale();
</script>

<template>
  <button
    type="button"
    :tabindex="focusable ? 0 : -1"
    @keydown.space.prevent="focusable && emit('toggle')"
    @keydown.up.prevent="onArrow(-1)"
    @keydown.down.prevent="onArrow(1)"
    class="reorder-handle d-flex align-center justify-center"
    :aria-label="t(isGrabbed ? 'components.reorder_item_put_down' : 'components.reorder_item_pick_up', { title })"
  >
    <MdiFaIcon :size="16" :icon="isGrabbed ? 'fa-solid fa-arrow-down-arrow-up' : 'fa-solid fa-grip-dots-vertical'" />
  </button>
</template>

<style scoped lang="scss">
.reorder-handle:focus-visible {
  outline: 2px solid rgb(var(--v-theme-primary));
  outline-offset: 2px;
}

.reorder-handle {
  background: none;
  border: 0;
  cursor: grab;
}

.reorder-handle:active {
  cursor: grabbing;
}

.reorder-handle {
  /* 2.5.8 target size: this is the drag and pick-up target on touch. */
  min-width: 24px;
  min-height: 24px;
  touch-action: none;
}
</style>
